import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  ResetIcon,
  ChevronDownIcon,
  CheckIcon,
} from "../../../commonUI/Icons/SvgIcon";
import TooltipIcon from "../../../commonUI/Tooltip/TooltipIcon";
import Pagination from "../../../commonUI/Pagination/Pagination";
import NotificationDetailsPanel from "./NotificationDetailsPanel";
import {
  NOTIFICATION_HISTORY_DATA,
  NOTIFICATION_HISTORY_MESSAGE_FILTERS,
  NOTIFICATION_STATUS_FILTERS,
} from "../../../../config/notificationConstants";
import { Box, Drawer } from "@mui/material";

// ── Simple dropdown (local to this file) ─────────────────────────────────────
const HistoryDropdown = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div className="notif-dropdown" ref={ref} style={{ minWidth: 180 }}>
      <button
        className={`notif-dropdown__btn${open ? " notif-dropdown__btn--open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span>{selected.label}</span>
        <ChevronDownIcon className="notif-dropdown__chevron" />
      </button>
      {open && (
        <div className="notif-dropdown__menu">
          {options.map((opt) => (
            <button
              key={opt.value}
              className={`notif-dropdown__item${value === opt.value ? " notif-dropdown__item--active" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              type="button"
            >
              <span className="notif-dropdown__check">
                {value === opt.value && <CheckIcon width={12} height={12} />}
              </span>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Channel badge ─────────────────────────────────────────────────────────────
const ChannelBadge = ({ channel }) => {
  const cls =
    channel === "SMS"
      ? "notif-channel-badge notif-channel-badge--sms"
      : channel === "Push"
        ? "notif-channel-badge notif-channel-badge--push"
        : "notif-channel-badge notif-channel-badge--email";
  const label =
    channel === "SMS" ? "✉ SMS" : channel === "Push" ? "🔔 Push" : "✉ Email";
  return <span className={cls}>{label}</span>;
};

// ── Status badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cls =
    status === "Delivered"
      ? "notif-status-badge notif-status-badge--delivered"
      : status === "Failed to send"
        ? "notif-status-badge notif-status-badge--failed"
        : "notif-status-badge notif-status-badge--unconfirmed";
  return <span className={cls}>{status}</span>;
};

// ── Recipient avatar with initials ────────────────────────────────────────────
const RecipientAvatar = ({ recipient }) => (
  <div className="notif-recipient">
    <div
      className="notif-recipient__avatar"
      style={{ backgroundColor: recipient.avatarColor }}
    >
      {recipient.initials}
    </div>
    <div className="notif-recipient__info">
      <span className="notif-recipient__name">{recipient.name}</span>
      <span className="notif-recipient__id">{recipient.id}</span>
    </div>
  </div>
);

// ── Main History component ────────────────────────────────────────────────────
const NotificationHistory = ({ selectedCategory, selectedChannel }) => {
  const [search, setSearch] = useState("");
  const [messageFilter, setMessageFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [inputValue, setInputValue] = useState(1);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentView, setCurrentView] = useState(null);

  const toggleDrawer = useCallback((open, view = null) => {
    setDrawerOpen(open);
    if (open) {
      setCurrentView(view);
    }
  }, []);
  const handleReset = () => {
    setSearch("");
    setMessageFilter("all");
    setStatusFilter("all");
    setPage(1);
    setInputValue(1);
    setPageSize(10);
  };

  const normalizeMessageName = (message) => {
    return message.toLowerCase().replace(/\s+/g, "-");
  };

  const filteredData = NOTIFICATION_HISTORY_DATA.filter((row) => {
    if (selectedCategory !== "all" && row.categoryId !== selectedCategory)
      return false;
    if (selectedChannel !== "all" && row.channel !== selectedChannel)
      return false;
    if (
      messageFilter !== "all" &&
      normalizeMessageName(row.message) !== messageFilter
    )
      return false;
    if (statusFilter !== "all" && row.status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (
        !row.recipient.name.toLowerCase().includes(q) &&
        !row.recipient.id.toLowerCase().includes(q) &&
        !row.message.toLowerCase().includes(q)
      ) {
        return false;
      }
    }
    return true;
  });

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = filteredData.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  return (
    <>
      {/* History filter row */}
      <div className="notif-history-filters">
        <div className="notif-history-filters__search-wrap">
          <div className="notif-history-filters__search-label">Search</div>
          <div className="notif-history-filters__search-input-wrap">
            <input
              className="notif-history-filters__search-input"
              type="text"
              placeholder="Recipient or metadata"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div>
          <div className="notif-history-filters__dropdown-label">Message</div>
          <HistoryDropdown
            options={NOTIFICATION_HISTORY_MESSAGE_FILTERS}
            value={messageFilter}
            onChange={setMessageFilter}
          />
        </div>

        <div>
          <div className="notif-history-filters__dropdown-label">Status</div>
          <HistoryDropdown
            options={NOTIFICATION_STATUS_FILTERS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </div>

        <button
          className="notif-filter-bar__reset-btn"
          onClick={handleReset}
          type="button"
        >
          <ResetIcon width="15" height="15" />
          Reset
        </button>
      </div>

      {/* History table */}
      <div className="notif-history-table-section">
        <table className="notif-table notif-history-table">
          <thead>
            <tr className="notif-table__head-row">
              <th className="notif-table__head-cell" style={{ width: 160 }}>
                Time
              </th>
              <th className="notif-table__head-cell">Recipient</th>
              <th className="notif-table__head-cell">Message</th>
              <th className="notif-table__head-cell" style={{ width: 110 }}>
                Channel
              </th>
              <th className="notif-table__head-cell" style={{ width: 160 }}>
                Status{" "}
                <TooltipIcon
                  title="Delivery status of the notification"
                  inline
                />
              </th>
              <th className="notif-table__head-cell" style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan={6} className="notif-history-table__empty">
                  No notifications match your filters.
                </td>
              </tr>
            ) : (
              paginatedData.map((row) => (
                <tr key={row.id} className="notif-table__row">
                  <td className="notif-table__cell">
                    <div className="notif-history-time">
                      <span className="notif-history-time__date">
                        {row.date}
                      </span>
                      <span className="notif-history-time__time">
                        {row.time}
                      </span>
                    </div>
                  </td>
                  <td className="notif-table__cell">
                    <RecipientAvatar recipient={row.recipient} />
                  </td>
                  <td className="notif-table__cell">
                    <div className="notif-history-message">
                      <span className="notif-history-message__category">
                        {row.category}
                      </span>
                      <span className="notif-history-message__name">
                        {row.message}
                      </span>
                    </div>
                  </td>
                  <td className="notif-table__cell">
                    <ChannelBadge channel={row.channel} />
                  </td>
                  <td className="notif-table__cell">
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="notif-table__cell">
                    <button
                      className="notif-history-table__details-btn"
                      type="button"
                      onClick={() => {
                        setSelectedNotification(row);
                        toggleDrawer(true, "details");
                      }}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        pageSize={pageSize}
        totalPages={totalPages}
        page={page}
        inputValue={inputValue}
        setInputValue={setInputValue}
        setPageSize={setPageSize}
        setPage={setPage}
      />

      {/* Notification Details Panel */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => toggleDrawer(false)}
        ModalProps={{
          BackdropProps: {
            style: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
          },
        }}
        sx={{
          boxShadow: "none",
          "& .MuiDrawer-paper": { boxShadow: "none" },
        }}
      >
        <Box
          sx={{ width: 600 }}
          role="presentation"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <NotificationDetailsPanel
            notification={selectedNotification}
            onClose={() => {
              setSelectedNotification(null);
              toggleDrawer(false);
            }}
          />
        </Box>
      </Drawer>
    </>
  );
};

export default NotificationHistory;
